import { apiFetch } from "./apiFetch";
import { BASE_URL_V2 } from "./constants";

/**
 * The member-facing join resolver, borrowed by the panel so an admin can drop
 * into a session straight from the Video Platform list.
 *
 * Deliberately not built locally from `externalMeetingId`, even though the
 * list already carries it:
 *
 *  - It applies the `useFallback` Google Meet decision at access time, so the
 *    panel, apps-video and apps-focused all send a person to the same place
 *    for the same session — one source of truth instead of three.
 *  - It embeds a silent pre-auth token, so the admin lands on the video
 *    platform already signed in. That matters here more than anywhere: the
 *    video app is a different origin with its own browser session, so without
 *    it a Join would drop the admin on a sign-in screen.
 *  - It needs no per-environment video-URL variable.
 *
 * The route admits `Admins` and `Hosts` alongside `Clients`.
 *
 * Not read-only: resolving a join records a `meeting_joined` event, so admin
 * joins from the panel do land in join analytics. Call it on an actual join.
 */
export interface MeetingJoinResponse {
  /**
   * Where the session actually opens. For a video-platform session this is
   * `{VIDEO_URL_BASE}/meeting/{externalMeetingId}`, possibly with a `#ml=`
   * pre-auth fragment; for a session flipped to a Google Meet fallback it is
   * that Meet URL — which, being typed by hand in the panel and stored
   * verbatim, is not guaranteed to carry a scheme. Normalize before navigating
   * (see `joinTarget.ts`).
   */
  joinUrl: string;
}

export const UserMeetingResource = {
  resolveJoinUrl: (meetingId: string) =>
    apiFetch<MeetingJoinResponse>(
      `/user/meeting/${encodeURIComponent(meetingId)}/join`,
      { baseUrl: BASE_URL_V2 }
    ),
};
